import type { SupabaseClient } from '../../lib/supabase';
import { PROJECT_WORKFLOW_LABELS, type ProjectWorkflowStatus } from '../projects/types';

/**
 * A project's own history: status, due date, owner and membership. Written
 * only by database triggers, read only by people who can see the project.
 *
 * These render as quiet system lines inside the Discussion thread rather than
 * in a tab of their own — the redesign dropped the Activity tab on the grounds
 * that a separate feed nobody opens is worse than a trail beside the
 * conversation it explains.
 */

export type ProjectActivityEvent =
  'status_changed' | 'due_date_changed' | 'owner_changed' | 'member_added' | 'member_removed';

export interface ProjectActivityRow {
  id: string;
  event_type: ProjectActivityEvent;
  details: Record<string, unknown>;
  actor_id: string | null;
  actor_name: string;
  created_at: string;
}

/** Reads a jsonb value as a display string; absent and null both read empty. */
function detailText(details: Record<string, unknown>, key: string): string {
  const value = details[key];
  return typeof value === 'string' ? value : '';
}

/**
 * The sentence a system line shows. Names come from the lookup rather than the
 * stored row, so a person who changes their display name reads correctly in
 * history they wrote months ago.
 */
export function activitySentence(row: ProjectActivityRow, nameById: Map<string, string>): string {
  const person = (key: string): string => {
    const id = detailText(row.details, key);
    return id === '' ? 'someone' : (nameById.get(id) ?? 'someone');
  };

  switch (row.event_type) {
    case 'status_changed': {
      const to = detailText(row.details, 'to_status');
      const label = PROJECT_WORKFLOW_LABELS[to as ProjectWorkflowStatus];
      return label === undefined ? 'changed the status' : `changed the status to ${label}`;
    }
    case 'due_date_changed': {
      const to = detailText(row.details, 'to_date');
      return to === '' ? 'cleared the due date' : `set the due date to ${to}`;
    }
    case 'owner_changed':
      return `made ${person('to_owner_id')} the owner`;
    case 'member_added':
      return `added ${person('member_id')}`;
    case 'member_removed':
      return `removed ${person('member_id')}`;
  }
}

const ACTIVITY_COLUMNS =
  'id, event_type, details, actor_id, created_at, profiles:actor_id (display_name, email)';

interface ActivityRow {
  id: string;
  event_type: ProjectActivityEvent;
  details: Record<string, unknown> | null;
  actor_id: string | null;
  created_at: string;
  profiles: { display_name: string; email: string } | null;
}

/** The most recent history for one project, newest last to match the thread. */
export async function fetchProjectActivity(
  client: SupabaseClient,
  projectId: string,
): Promise<ProjectActivityRow[]> {
  const { data, error } = await client
    .from('project_activity')
    .select(ACTIVITY_COLUMNS)
    .eq('project_id', projectId)
    .order('created_at', { ascending: true })
    .limit(200);

  if (error) {
    throw new Error(error.message);
  }

  return ((data ?? []) as unknown as ActivityRow[]).map((row) => ({
    id: row.id,
    event_type: row.event_type,
    details: row.details ?? {},
    actor_id: row.actor_id,
    actor_name: row.profiles
      ? row.profiles.display_name.trim() !== ''
        ? row.profiles.display_name
        : row.profiles.email
      : 'The system',
    created_at: row.created_at,
  }));
}
